/**
 * NAO checkout — contrato con el backend externo (Vercel).
 * IMPORTANTE: el shape del payload está acoplado a ese backend
 * (repo `nao-checkout-backend`, api/index.js). No renombrar/quitar campos sin
 * coordinar allí.
 *
 * Campos nuevos respecto a la versión de un solo producto:
 *   items           una línea por aroma: [{ variant_id, quantity }]. Solo viaja
 *                   en los productos con selector de aromas; sin él, el backend
 *                   sigue el camino de siempre con `variant_id` + `quantity`.
 *   quantity        unidades del pedido (antes el backend asumía 1).
 *   shipping_cost   envío en pesos que se le mostró al cliente.
 *   expected_total  total exacto que vio el cliente, en pesos.
 * El backend recalcula envío y total por su cuenta y rechaza la orden si no
 * coinciden con estos dos valores, para que el cobro nunca se separe de lo que
 * la pantalla prometió.
 */

const CHECKOUT_ENDPOINT = 'https://nao-checkout-v2.vercel.app/api/index';

// Construye el payload exacto que espera el backend a partir del FormData.
// `options`: { shippingCost, quantity, isCOD, unitPrice, honeypot }
export function buildPayload(formData, options) {

    const opts = options || {};

    const quantity = Math.max(1, parseInt(opts.quantity, 10) || 1);
    const shippingCost = Math.max(0, parseInt(opts.shippingCost, 10) || 0);
    const unitPrice = Math.max(0, parseInt(opts.unitPrice, 10) || 0);

    // El método lo decide la selección del cliente, no el costo de envío: con
    // las reglas por cantidad el envío puede ser 0 en contraentrega (kit) y
    // distinto de 0 en anticipado.
    const paymentMethod = opts.isCOD ? 'Contra Entrega' : 'Anticipado';

    // Una línea por aroma. Se filtran las de cantidad 0 para no mandar ruido, y
    // el backend además fusiona repetidos y comprueba que la suma cuadre con
    // `quantity`: si no, rechaza con 409 en vez de despachar otra cosa.
    const items = (Array.isArray(opts.items) ? opts.items : [])
        .filter(line => line && line.variantId && line.quantity > 0)
        .map(line => ({
            variant_id: String(line.variantId),
            quantity: line.quantity
        }));

    // El subtotal lo manda quien conoce los precios de cada línea; sin aromas
    // sigue siendo precio unitario por cantidad, como hasta ahora.
    const subtotal = opts.subtotal === undefined
        ? unitPrice * quantity
        : Math.max(0, Math.round(opts.subtotal));

    const payload = {

        // Con aromas el `variant_id` es el del primero elegido: el backend lo
        // ignora en cuanto ve `items`, pero se manda para que un despliegue del
        // tema por delante del backend siga creando un pedido válido.
        variant_id:
            items.length ? items[0].variant_id : formData.get('variant_id'),

        quantity:
            quantity,

        // Un solo campo en el formulario; el backend lo parte por el primer
        // espacio para separar nombre y apellido en la dirección de envío.
        name:
            (formData.get('full_name') || '').trim(),

        phone:
            formData.get('phone') || '',

        whatsapp:
            formData.get('whatsapp_phone') ||
            formData.get('phone') ||
            '',

        address:
            formData.get('address') || '',

        address_details:
            formData.get('complement') || '',

        city:
            formData.get('city') || '',

        department:
            formData.get('province') || '',

        email:
            formData.get('email') || '',

        payment_method:
            paymentMethod,

        shipping_cost:
            shippingCost,

        expected_total:
            subtotal + shippingCost,

        honeypot:
            opts.honeypot || ''
    };

    if (items.length) {
        payload.items = items;
    }

    return payload;
}

export async function submitOrder(payload) {

    const response = await fetch(CHECKOUT_ENDPOINT,
        {
            method: 'POST',

            headers: {
                'Content-Type': 'application/json'
            },

            body: JSON.stringify(payload)
        }
    );

    return response.json();
}
